import { useState } from "react";
import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { setProp, setTaskType } from "../core/ops";
import { formatRrule, parseRrule, type NthKind, type Rule } from "../core/rrule";
import type { Task } from "../core/schema";
import { Input, Select } from "../ui";
import { isPeriodUnit, PERIOD_UNITS as UNITS, periodIn, type PeriodUnit as Unit } from "./period";

type Mode = "none" | "soft" | "fixed";
const FREQS: Rule["freq"][] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
/** Monday first; the numbers are the rule's weekday numbers (0 = Sunday) */
const WEEK = [1, 2, 3, 4, 5, 6, 0];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const chip =
  "h-[26px] min-w-9 rounded-sm border border-neutral-200 px-1.5 text-[12px] text-neutral-600 aria-pressed:border-accent aria-pressed:bg-accent-tint aria-pressed:text-accent dark:border-neutral-800 dark:text-neutral-300";

/**
 * *Repeats* (spec §9.5): no; *on an elastic schedule* — a soft repeating task (`recurring`, every so many minutes,
 * hours, days, weeks or months, the want bar); or *on a fixed schedule* — `repeat.rrule`, written from the choices
 * here (weekdays; for monthly ones the months and the nth day, workday or non-workday; the time the next one
 * appears), the next one made when this one is done. Replaces typing a calendar rule by hand.
 */
export function RepeatEditor({
  task,
  ro,
  write,
  allowFixed,
}: {
  task: Task;
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
  /** the fixed schedule is a `standard` field: shown from the task panel's Normal level */
  allowFixed: boolean;
}) {
  const { t, i18n } = useTranslation();
  const rrule = typeof task.props["repeat.rrule"] === "string" ? (task.props["repeat.rrule"] as string) : "";
  const rule = rrule ? parseRrule(rrule) : null;
  const mode: Mode = task.typeId === "recurring" ? "soft" : rule ? "fixed" : "none";
  const periodDays = Number(task.props["rec.periodDays"]) || 7;
  const unit: Unit = isPeriodUnit(task.props["rec.periodUnit"]) ? task.props["rec.periodUnit"] : "days";
  const [amount, setAmount] = useState(String(periodIn(periodDays, unit)));
  const [every, setEvery] = useState(String(rule?.interval ?? 1));

  const setMode = (next: Mode) =>
    write((doc) => {
      if (next === "soft") {
        const n = Number(amount.replace(",", "."));
        setProp(doc, task.id, "repeat.rrule", null);
        setProp(doc, task.id, "rec.periodDays", n > 0 ? n / UNITS[unit] : 7);
        if (task.typeId !== "recurring") setTaskType(doc, task.id, "recurring");
        return;
      }
      if (task.typeId === "recurring") setTaskType(doc, task.id, "task");
      // not repeating any more: no turns (the order stays in `assignee` for when it repeats again)
      if (next === "none") setProp(doc, task.id, "rec.rotation", null);
      setProp(
        doc,
        task.id,
        "repeat.rrule",
        next === "fixed" ? formatRrule({ freq: "WEEKLY", interval: 1, byDay: [] }) : null,
      );
    });
  const saveRule = (r: Rule) => write((doc) => setProp(doc, task.id, "repeat.rrule", formatRrule(r)));
  /** the elastic period from what is typed and the unit chosen */
  const savePeriod = (value: string, u: Unit) => {
    const n = Number(value.replace(",", "."));
    if (!(n > 0)) return;
    const days = n / UNITS[u];
    if (days === periodDays && u === unit) return;
    write((doc) => {
      setProp(doc, task.id, "rec.periodDays", days);
      setProp(doc, task.id, "rec.periodUnit", u === "days" ? null : u);
    });
  };
  const dayName = (d: number) =>
    // 2024-01-07 was a Sunday: the weekday's short name in the person's language
    new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(new Date(2024, 0, 7 + d));
  const monthName = (m: number) =>
    new Intl.DateTimeFormat(i18n.language, { month: "short" }).format(new Date(2024, m - 1, 1));
  const nthMax = rule?.nth && rule.nth.kind !== "day" ? 5 : 31;
  const time = rule?.time
    ? `${String(rule.time.h).padStart(2, "0")}:${String(rule.time.m).padStart(2, "0")}`
    : "";

  return (
    <div className="col-span-2 flex flex-col gap-1.5" data-testid="repeat-editor">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-neutral-500">{t("repeat.label")}</span>
        <Select
          compact
          disabled={ro}
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
          aria-label={t("repeat.label")}
          data-testid="repeat-mode"
        >
          <option value="none">{t("repeat.none")}</option>
          <option value="soft">{t("repeat.soft")}</option>
          {(allowFixed || mode === "fixed") && <option value="fixed">{t("repeat.fixed")}</option>}
        </Select>
      </div>
      {mode === "soft" && (
        <div className="flex items-center gap-2">
          <span className="text-[13px]">{t("repeat.every")}</span>
          <Input
            compact
            className="w-16"
            inputMode="decimal"
            disabled={ro}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={(e) => savePeriod(e.target.value, unit)}
            aria-label={t("repeat.interval")}
            data-testid="repeat-days"
          />
          <Select
            compact
            disabled={ro}
            value={unit}
            onChange={(e) => savePeriod(amount, e.target.value as Unit)}
            aria-label={t("repeat.unit")}
            data-testid="repeat-unit"
          >
            {(Object.keys(UNITS) as Unit[]).map((u) => (
              <option key={u} value={u}>
                {t(`repeat.units.${u}`, { count: Number(amount.replace(",", ".")) || 2 })}
              </option>
            ))}
          </Select>
        </div>
      )}
      {mode === "fixed" && rule && (
        <div className="flex items-center gap-2">
          <span className="text-[13px]">{t("repeat.every")}</span>
          <Input
            compact
            className="w-14"
            inputMode="numeric"
            disabled={ro}
            value={every}
            onChange={(e) => setEvery(e.target.value)}
            onBlur={(e) => {
              const n = Math.max(1, Math.round(Number(e.target.value) || 1));
              setEvery(String(n));
              if (n !== rule.interval) saveRule({ ...rule, interval: n });
            }}
            aria-label={t("repeat.interval")}
            data-testid="repeat-interval"
          />
          <Select
            compact
            disabled={ro}
            value={rule.freq}
            onChange={(e) => saveRule({ ...rule, freq: e.target.value as Rule["freq"] })}
            aria-label={t("repeat.unit")}
            data-testid="repeat-freq"
          >
            {FREQS.map((f) => (
              <option key={f} value={f}>
                {t(`repeat.freq.${f}`, { count: rule.interval })}
              </option>
            ))}
          </Select>
        </div>
      )}
      {mode === "fixed" && rule?.freq === "WEEKLY" && (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t("repeat.weekdays")}>
          <span className="mr-1 text-[12.5px] text-neutral-500">{t("repeat.on")}</span>
          {WEEK.map((d) => {
            const on = rule.byDay.includes(d);
            return (
              <button
                key={d}
                type="button"
                disabled={ro}
                aria-pressed={on}
                onClick={() =>
                  saveRule({ ...rule, byDay: on ? rule.byDay.filter((x) => x !== d) : [...rule.byDay, d] })
                }
                className={chip}
                data-testid="repeat-weekday"
              >
                {dayName(d)}
              </button>
            );
          })}
        </div>
      )}
      {mode === "fixed" && rule?.freq === "MONTHLY" && (
        <>
          {/* which months: none chosen means every month */}
          <div className="flex items-start gap-1" role="group" aria-label={t("repeat.months")}>
            <span className="mr-1 mt-1 text-[12.5px] text-neutral-500">{t("repeat.in")}</span>
            <div className="grid grid-cols-6 gap-1">
              {MONTHS.map((m) => {
                const on = rule.byMonth?.includes(m) ?? false;
                const months = rule.byMonth ?? [];
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={ro}
                    aria-pressed={on}
                    onClick={() =>
                      saveRule({ ...rule, byMonth: on ? months.filter((x) => x !== m) : [...months, m] })
                    }
                    className={chip}
                    data-testid="repeat-month"
                  >
                    {monthName(m)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="shrink-0 whitespace-nowrap text-[12.5px] text-neutral-500">
              {t("repeat.onThe")}
            </span>
            <Select
              compact
              disabled={ro}
              value={rule.nth ? String(rule.nth.n) : ""}
              onChange={(e) => {
                const n = Number(e.target.value);
                saveRule({ ...rule, nth: e.target.value ? { kind: rule.nth?.kind ?? "day", n } : null });
              }}
              aria-label={t("repeat.nth")}
              data-testid="repeat-nth-n"
            >
              <option value="">{t("repeat.sameDay")}</option>
              {Array.from({ length: nthMax }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {t("repeat.ordinal", { n })}
                </option>
              ))}
              <option value="-1">{t("repeat.last")}</option>
            </Select>
            {rule.nth && (
              <Select
                compact
                disabled={ro}
                value={rule.nth.kind}
                onChange={(e) => {
                  const kind = e.target.value as NthKind;
                  // workdays and non-workdays count to 5 at most
                  const n = kind !== "day" && rule.nth!.n > 5 ? 5 : rule.nth!.n;
                  saveRule({ ...rule, nth: { kind, n } });
                }}
                aria-label={t("repeat.nthKind")}
                data-testid="repeat-nth-kind"
              >
                {(["day", "workday", "nonworkday"] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`repeat.nthKinds.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </>
      )}
      {mode === "fixed" && rule && (
        <div className="flex items-center gap-2">
          <span className="shrink-0 whitespace-nowrap text-[12.5px] text-neutral-500">
            {t("repeat.appearsAt")}
          </span>
          <Input
            compact
            type="time"
            className="w-[7rem]"
            disabled={ro}
            value={time}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":").map(Number);
              saveRule({
                ...rule,
                time: e.target.value && Number.isFinite(h) && Number.isFinite(m) ? { h: h!, m: m! } : null,
              });
            }}
            aria-label={t("repeat.appearsAt")}
            data-testid="repeat-time"
          />
          <span className="text-[12px] text-neutral-500">{t("repeat.appearsAtHint")}</span>
        </div>
      )}
      <p className="text-[12px] text-neutral-500">
        {mode === "soft" ? t("repeat.softHint") : mode === "fixed" ? t("repeat.fixedHint") : ""}
      </p>
    </div>
  );
}
